declare global {
  interface Window {
    daum?: {
      Postcode: new (options: {
        oncomplete: (data: {
          zonecode: string;
          roadAddress: string;
          jibunAddress: string;
        }) => void;
        width?: string | number;
        height?: string | number;
      }) => { embed: (el: HTMLElement) => void };
    };
  }
}

const SCRIPT_SRC = "https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";

let scriptPromise: Promise<void> | null = null;

function loadDaumPostcodeScript(): Promise<void> {
  if (window.daum?.Postcode) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error("주소 검색 서비스를 불러오지 못했습니다."));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
}

/** Opens the Daum Postcode search as an in-page embedded overlay (not a
 *  window.open popup, which mobile/desktop browsers routinely block once
 *  the script-load await breaks the click's user-gesture chain). Resolves
 *  with the selected postal code + address (roadAddress preferred,
 *  jibunAddress fallback), or never resolves if the user closes the
 *  overlay without selecting. */
export async function openDaumPostcode(): Promise<{ postalCode: string; address: string }> {
  await loadDaumPostcodeScript();

  return new Promise((resolve) => {
    const layer = document.createElement("div");
    layer.style.cssText =
      "position:fixed;inset:0;z-index:9999;background:rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center;padding:16px;";

    const box = document.createElement("div");
    box.style.cssText =
      "position:relative;width:100%;max-width:480px;height:min(70vh,600px);background:#fff;border-radius:8px;overflow:hidden;";

    const closeBtn = document.createElement("button");
    closeBtn.textContent = "✕";
    closeBtn.setAttribute("aria-label", "닫기");
    closeBtn.style.cssText =
      "position:absolute;top:4px;right:4px;z-index:1;border:none;background:transparent;font-size:18px;line-height:1;cursor:pointer;padding:8px;color:#333;";

    box.appendChild(closeBtn);
    layer.appendChild(box);
    document.body.appendChild(layer);

    const cleanup = () => {
      if (layer.parentNode === document.body) {
        document.body.removeChild(layer);
      }
    };
    closeBtn.onclick = cleanup;
    layer.addEventListener("click", (e) => {
      if (e.target === layer) cleanup();
    });

    new window.daum!.Postcode({
      width: "100%",
      height: "100%",
      oncomplete: (data) => {
        cleanup();
        resolve({
          postalCode: data.zonecode,
          address: data.roadAddress || data.jibunAddress,
        });
      },
    }).embed(box);
  });
}
